import React from 'react';
import { PolarAngleAxis, RadialBar, RadialBarChart, ResponsiveContainer } from 'recharts';
import { CHART_COLORS, formatUsdFull } from './common';

interface GaugeChartProps {
  /** 0–100, already clamped by the caller. */
  percent: number;
  /** Big number under the dial. */
  primaryLabel: string;
  primaryValue: number;
  /** The gap the dial is warning about; rendered in the risk hue when non-zero. */
  secondaryLabel: string;
  secondaryValue: number;
  valueFormatter?: (value: number) => string;
}

/**
 * Collection-rate dial. A ratio against a target reads faster as an arc than
 * as two bars — the eye judges "nearly full" without parsing either number —
 * and the shortfall underneath is what actually needs chasing.
 */
const GaugeChart: React.FC<GaugeChartProps> = ({
  percent,
  primaryLabel,
  primaryValue,
  secondaryLabel,
  secondaryValue,
  valueFormatter = formatUsdFull,
}) => {
  const complete = percent >= 99.95;
  const arcColor = complete ? CHART_COLORS.in : percent >= 80 ? CHART_COLORS.agent : CHART_COLORS.credits;

  return (
    <div className="flex h-full items-center gap-3">
      <div className="relative h-full w-1/2 min-w-[128px]">
        <ResponsiveContainer width="100%" height="100%">
          <RadialBarChart
            data={[{ name: 'collected', value: percent, fill: arcColor }]}
            innerRadius="68%"
            outerRadius="100%"
            startAngle={210}
            endAngle={-30}
            barSize={14}
          >
            <PolarAngleAxis type="number" domain={[0, 100]} angleAxisId={0} tick={false} />
            <RadialBar
              background={{ fill: '#eff6ff' }}
              dataKey="value"
              cornerRadius={8}
              isAnimationActive={false}
            />
          </RadialBarChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-semibold tabular-nums text-blue-950">
            {percent.toFixed(percent % 1 === 0 ? 0 : 1)}%
          </span>
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
            collected
          </span>
        </div>
      </div>

      <dl className="flex-1 space-y-3 text-xs">
        <div>
          <dt className="text-muted-foreground">{primaryLabel}</dt>
          <dd className="tabular-nums text-sm font-semibold text-blue-950">
            {valueFormatter(primaryValue)}
          </dd>
        </div>
        <div>
          <dt className="text-muted-foreground">{secondaryLabel}</dt>
          <dd
            className={
              secondaryValue > 0
                ? 'tabular-nums text-sm font-semibold text-rose-700'
                : 'tabular-nums text-sm font-semibold text-blue-950'
            }
          >
            {valueFormatter(secondaryValue)}
          </dd>
        </div>
      </dl>
    </div>
  );
};

export default GaugeChart;
